
import { useState, useCallback } from 'react';
import { PromptStage } from './dice/useRollerState';

// Define all possible stages of the journal flow
export type JournalStage = 'stuck' | 'perception' | 'intention' | 'nextStep' | 'completed' | 'twist';

interface UseJournalFlowOptions {
  initialStage?: JournalStage;
  onStageChange?: (stage: JournalStage) => void;
}

export const useJournalFlow = ({ 
  initialStage = 'stuck',
  onStageChange 
}: UseJournalFlowOptions = {}) => {
  const [currentStage, setCurrentStage] = useState<JournalStage>(initialStage);

  // Function to set stage directly (useful for initialization)
  const setStage = useCallback((stage: JournalStage) => {
    setCurrentStage(stage);
    if (onStageChange) {
      onStageChange(stage);
    }
  }, [onStageChange]);

  // Function to advance to the next stage
  const advanceStage = useCallback(() => {
    setCurrentStage(prevStage => {
      let nextStage: JournalStage;
      
      switch (prevStage) {
        case 'stuck':
          nextStage = 'perception';
          break;
        case 'perception':
          nextStage = 'intention';
          break;
        case 'intention':
          nextStage = 'nextStep';
          break;
        case 'nextStep':
          nextStage = 'completed';
          break;
        case 'twist':
        case 'completed':
        default:
          nextStage = 'completed';
          break;
      }
      
      if (onStageChange) {
        onStageChange(nextStage);
      }
      
      return nextStage;
    });
  }, [onStageChange]);

  // Function to reset flow to a specific stage (or 'stuck' by default)
  const resetFlow = useCallback((stage: JournalStage = 'stuck') => {
    setCurrentStage(stage);
    if (onStageChange) {
      onStageChange(stage);
    }
  }, [onStageChange]);

  // Function to handle external stage changes (e.g., from dice roller)
  const handleExternalStageChange = useCallback((promptStage: PromptStage) => {
    // If promptStage is 'none', don't change the journal stage
    if (!promptStage || promptStage === 'none') return;
    
    // Map the promptStage to a journalStage
    let journalStage: JournalStage;
    
    switch (promptStage) {
      case 'perception':
        journalStage = 'perception';
        break;
      case 'intention':
        journalStage = 'intention';
        break;
      case 'nextStep':
        journalStage = 'nextStep';
        break;
      case 'twist':
        journalStage = 'twist';
        break;
      case 'lensFocus':
        // Currently don't update journal stage for lensFocus
        return;
      default:
        // If it's not one of the recognized stages, don't update
        return;
    }
    
    // Update the stage directly
    setCurrentStage(journalStage);
    
    if (onStageChange) {
      onStageChange(journalStage);
    }
  }, [onStageChange]);

  return {
    currentStage,
    setStage,
    advanceStage,
    resetFlow,
    handleExternalStageChange
  };
};
